import type { FC } from 'react';
import { Accordion, AccordionSummary } from '@interstate/components/AccordionGroup';
import type { EmployeeDetailsSupportedRoles, EmployeeTeam } from '@makemydeal/dr-dash-types';
import {
    AdditionalRolesBody,
    AdditionalRolesContainer,
    AdditionalRolesCountBadge,
    AdditionalRolesDescription,
    AdditionalRolesTopDivider
} from './EmployeeDetailSlideOut.style';
import { EmployeeRoleFields } from './EmployeeRoleFields';
import { additionalRoleTitleMap } from './employeeRoleUtils';
import type { InvalidFields, RoleFieldHandlers } from './useEmployeeRoleHandlers';

const INTRO_SENTENCE = 'These roles came back with data from the deal and are shown so you can review or correct them.';

export type AdditionalRolesSectionProps = {
    roles: EmployeeDetailsSupportedRoles[];
    isExpanded: boolean;
    onToggle: () => void;
    employeeDetails: EmployeeTeam;
    invalidFields: InvalidFields;
    handlers: RoleFieldHandlers;
};

export const AdditionalRolesSection: FC<AdditionalRolesSectionProps> = ({
    roles,
    isExpanded,
    onToggle,
    employeeDetails,
    invalidFields,
    handlers
}) => {
    return (
        <AdditionalRolesContainer data-testid="additional-roles-section">
            <AdditionalRolesTopDivider data-testid="additional-roles-top-divider" />
            <Accordion
                expanded={isExpanded}
                onChange={onToggle}
                data-testid="additional-roles-accordion"
                sx={{ borderBottomStyle: 'none', borderBottomWidth: 0, marginBottom: 0 }}
            >
                <AccordionSummary
                    chevronPosition="left"
                    data-testid="additional-roles-toggle"
                    badge={
                        <AdditionalRolesCountBadge data-testid="additional-roles-count">
                            {`${roles.length} found on deal`}
                        </AdditionalRolesCountBadge>
                    }
                >
                    Additional Roles
                </AccordionSummary>
                <AdditionalRolesBody
                    data-testid="additional-roles-body"
                    sx={{ paddingTop: 0, paddingRight: 0, paddingBottom: '2rem', paddingLeft: 0 }}
                >
                    <AdditionalRolesDescription data-testid="additional-roles-description">
                        {INTRO_SENTENCE}
                    </AdditionalRolesDescription>
                    {roles.map((role) => (
                        <EmployeeRoleFields
                            key={role as string}
                            role={role}
                            title={additionalRoleTitleMap[role as string]}
                            employee={employeeDetails[role]}
                            invalidFields={invalidFields.fields}
                            hasRoleError={invalidFields.roles.has(role as string)}
                            handlers={handlers}
                        />
                    ))}
                </AdditionalRolesBody>
            </Accordion>
        </AdditionalRolesContainer>
    );
};
